import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Send, Trash2, FileText, ImageIcon, Eye, Pencil, Images } from "lucide-react";
import { Wand2, Calendar, RefreshCw } from "lucide-react";
import ImageDropZone from "@/components/admin/ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";
import DOMPurify from "dompurify";
import SeoScorePanel, { type SeoIssueId } from "./SeoScorePanel";
import MetaPanel from "./MetaPanel";
import RichTextEditor from "./RichTextEditor";
import ImageGridPicker from "./ImageGridPicker";
import ArticleAuthorHeader from "@/components/ArticleAuthorHeader";

interface BlogArticle {
  id: string; slug: string; title: string; h1: string; meta_description: string; excerpt: string;
  body_html: string; keywords: string[];
  internal_link_url: string; internal_link_anchor: string;
  external_link_url: string; external_link_anchor: string;
  status: string; published_at: string | null;
  author_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}
interface BlogImage { id: string; slot: number; image_url: string; alt_text: string; title: string; description: string; }
interface Author { id: string; name: string; }

const ArticleAudit = ({ articleId, onBack }: { articleId: string; onBack: () => void }) => {
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [images, setImages] = useState<BlogImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [regenSlot, setRegenSlot] = useState<number | null>(null);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [pickerOpen, setPickerOpen] = useState<{ slot: number | null }>({ slot: null });
  const [seoKeyword, setSeoKeyword] = useState("");
  const [secondaryKeyword, setSecondaryKeyword] = useState("");
  const [busyIssue, setBusyIssue] = useState<SeoIssueId | "all" | "link" | null>(null);
  const [metaAiBusy, setMetaAiBusy] = useState<string | null>(null);
  const [regeneratingBody, setRegeneratingBody] = useState(false);
  const [fixAllProgress, setFixAllProgress] = useState<{ current: number; total: number; label: string } | null>(null);

  const load = async () => {
    const { data: a } = await (supabase as any).from("blog_articles").select("*").eq("id", articleId).maybeSingle();
    setArticle(a || null);
    if (a) {
      setSeoKeyword((a.keywords?.[0]) || "");
      setSecondaryKeyword((a.keywords?.[1]) || "");
    }
    const { data: imgs } = await (supabase as any).from("blog_article_images").select("*").eq("article_id", articleId).order("slot");
    setImages(imgs || []);
  };

  useEffect(() => { load(); }, [articleId]);
  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any).from("blog_authors").select("id,name").order("name");
      setAuthors(data || []);
    })();
  }, []);

  if (!article) return <Card><CardContent className="p-6">Chargement…</CardContent></Card>;
  // Slug verrouillé DÈS LA CRÉATION pour préserver le permalink (trigger DB equivalent).
  const slugLocked = true;

  const persistKeyword = async (kw: string) => {
    setSeoKeyword(kw);
    const rest = (article.keywords || []).slice(1);
    const newKeywords = [kw, ...rest].filter(Boolean);
    setArticle({ ...article, keywords: newKeywords });
    await (supabase as any).from("blog_articles").update({ keywords: newKeywords }).eq("id", articleId);
  };
  const persistSecondary = async (kw: string) => {
    setSecondaryKeyword(kw);
    const primary = (article.keywords?.[0]) || seoKeyword || "";
    const rest = (article.keywords || []).slice(2);
    const newKeywords = [primary, kw, ...rest].filter(Boolean);
    setArticle({ ...article, keywords: newKeywords });
    await (supabase as any).from("blog_articles").update({ keywords: newKeywords }).eq("id", articleId);
  };

  const save = async (extra: Partial<BlogArticle> = {}) => {
    setSaving(true);
    const payload = { ...article, ...extra };
    const update: any = {
      title: payload.title, h1: payload.h1, meta_description: payload.meta_description,
      excerpt: payload.excerpt, body_html: payload.body_html,
      author_id: payload.author_id ?? null,
    };
    // Slug never updated for published articles (DB trigger also enforces this).
    if (!slugLocked) update.slug = payload.slug;
    const { error } = await (supabase as any).from("blog_articles").update(update).eq("id", articleId);
    setSaving(false);
    if (error) toast.error(error.message);
    else { toast.success("Enregistré"); setArticle(payload as BlogArticle); }
  };

  const publish = async () => {
    setPublishing(true);
    const { error } = await (supabase as any).from("blog_articles").update({
      status: "published", published_at: new Date().toISOString(),
    }).eq("id", articleId);
    setPublishing(false);
    if (error) toast.error(error.message);
    else {
      toast.success("Article publié !");
      // Demande d'indexation Google instantanée (silencieuse si SA non configuré)
      const publicUrl = `https://maroctransfers.com/guide/blog/${article.slug}`;
      supabase.functions
        .invoke("google-index-notify", { body: { url: publicUrl, article_id: articleId } })
        .then(({ data, error: idxErr }) => {
          if (idxErr || (data as any)?.error) {
            toast.info("Indexation Google : configurez GOOGLE_INDEXING_SA_JSON pour l'auto-ping Search Console.");
          } else if ((data as any)?.ok) {
            toast.success("🚀 Demande d'indexation envoyée à Google Search Console.");
          }
        })
        .catch(() => {});
      load();
    }
  };
  const unpublish = async () => {
    const { error } = await (supabase as any).from("blog_articles").update({ status: "draft" }).eq("id", articleId);
    if (error) toast.error(error.message); else { toast.success("Repassé en brouillon"); load(); }
  };
  const remove = async () => {
    if (!confirm("Supprimer cet article définitivement ?")) return;
    const { error } = await (supabase as any).from("blog_articles").delete().eq("id", articleId);
    if (error) toast.error(error.message); else { toast.success("Supprimé"); onBack(); }
  };

  const uploadImage = async (slot: number, file: File): Promise<void> => {
    const webp = await compressToWebP(file, { maxSizeMB: 0.4 });
    const path = `blog-articles/${articleId}/img-${slot}-${Date.now()}.webp`;
    const { error: upErr } = await withUploadTimeout(
      supabase.storage.from("site-images").upload(path, webp, { contentType: webp.type, upsert: false })
    );
    if (upErr) throw new Error(upErr.message);
    const { data: pub } = supabase.storage.from("site-images").getPublicUrl(path);
    const url = `${pub.publicUrl}?t=${Date.now()}`;
    const existing = images.find((i) => i.slot === slot);
    const mainKeyword = (article.keywords?.[0] ?? article.title ?? "").toString();
    const altText = `${mainKeyword} — illustration ${slot}`.trim();
    if (existing) {
      const oldUrl = existing.image_url;
      await (supabase as any).from("blog_article_images").update({ image_url: url }).eq("id", existing.id);
      if (oldUrl && article.body_html?.includes(oldUrl)) {
        const newBody = article.body_html.split(oldUrl).join(url);
        await (supabase as any).from("blog_articles").update({ body_html: newBody }).eq("id", articleId);
      }
    } else {
      await (supabase as any).from("blog_article_images").insert({
        article_id: articleId, slot, image_url: url, alt_text: altText,
        title: `${mainKeyword} — ${article.title}`.slice(0, 200),
        description: `Image illustrant l'article "${article.title}".`.slice(0, 300),
      });
    }
    toast.success("Image mise à jour"); load();
  };
  const updateImageField = async (id: string, patch: Partial<BlogImage>) => {
    await (supabase as any).from("blog_article_images").update(patch).eq("id", id); load();
  };
  const onPickPhoto = async (slot: number, photo: { download_url: string; alt: string }) => {
    setRegenSlot(slot);
    try {
      const url = photo.download_url;
      const existing = images.find((i) => i.slot === slot);
      const mainKeyword = (article.keywords?.[0] ?? article.title ?? "").toString();
      const altText = photo.alt || `${mainKeyword} — illustration ${slot}`;
      if (existing) {
        const oldUrl = existing.image_url;
        await (supabase as any).from("blog_article_images").update({ image_url: url, alt_text: altText }).eq("id", existing.id);
        if (oldUrl && article.body_html?.includes(oldUrl)) {
          const newBody = article.body_html.split(oldUrl).join(url);
          await (supabase as any).from("blog_articles").update({ body_html: newBody }).eq("id", articleId);
        }
      } else {
        await (supabase as any).from("blog_article_images").insert({
          article_id: articleId, slot, image_url: url, alt_text: altText,
          title: `${mainKeyword} — ${article.title}`.slice(0, 200),
          description: `Image illustrant l'article "${article.title}".`.slice(0, 300),
        });
      }
      toast.success("Image mise à jour"); load();
    } catch (e: any) {
      toast.error(e?.message || "Échec");
    } finally { setRegenSlot(null); }
  };

  const fixIssue = async (id: SeoIssueId) => {
    if (id === "kw_in_slug" && slugLocked) {
      toast.warning("Permalink verrouillé — impossible de modifier le slug d'un article publié.");
      return;
    }
    setBusyIssue(id);
    try {
      const upd: any = {
        title: article.title, meta_description: article.meta_description,
        body_html: article.body_html,
        keywords: [seoKeyword, secondaryKeyword, ...((article.keywords || []).slice(2))].filter(Boolean),
      };
      if (!slugLocked) upd.slug = article.slug;
      await (supabase as any).from("blog_articles").update(upd).eq("id", articleId);
      const { data, error } = await supabase.functions.invoke("seo-fix-issue", { body: { articleId, issueId: id } });
      if (error) throw error;
      const field = data?.field as keyof BlogArticle | undefined;
      const value = data?.value;
      if (field && typeof value === "string") {
        setArticle((prev) => prev ? { ...prev, [field]: value } as BlogArticle : prev);
      }
      if (data?.applied === false) {
        toast.warning(`Correction partielle (${data?.reason || "contrainte non respectée"}). Cliquez à nouveau pour réessayer.`);
      } else {
        toast.success("Correction appliquée");
      }
    } catch (e: any) { toast.error(e?.message || "Échec"); }
    finally { setBusyIssue(null); }
  };

  const fixAll = async () => {
    const order: SeoIssueId[] = [
      "kw_in_title", "title_length", "title_starts_with_keyword", "power_words",
      "kw_in_slug", "kw_in_meta", "meta_length",
      "kw_in_intro", "kw_in_subheadings", "kw_density", "content_length",
      "paragraph_length", "sentence_length", "transition_words", "has_lists",
    ];
    const LABELS: Record<SeoIssueId, string> = {
      kw_in_title: "Mot-clé dans le titre",
      title_length: "Longueur du titre",
      title_starts_with_keyword: "Titre commence par le mot-clé",
      power_words: "Power word",
      kw_in_slug: "Mot-clé dans le slug",
      kw_in_meta: "Mot-clé dans la meta",
      meta_length: "Longueur de la meta",
      kw_in_intro: "Mot-clé dans l'intro",
      kw_in_subheadings: "Mot-clé dans les H2/H3",
      kw_density: "Densité du mot-clé",
      content_length: "Longueur du contenu (≥ 900 mots)",
      paragraph_length: "Longueur des paragraphes",
      sentence_length: "Longueur des phrases",
      transition_words: "Mots de transition",
      has_lists: "Listes à puces",
      has_images: "Images dans le contenu",
      kw_in_image_alt: "Mot-clé dans un ALT",
      outbound_authority_link: "Lien externe d'autorité",
      internal_link: "Liens internes",
    };
    setBusyIssue("all");
    setFixAllProgress({ current: 0, total: order.length + 1, label: "Initialisation…" });
    try {
      for (let i = 0; i < order.length; i++) {
        const id = order[i];
        setFixAllProgress({ current: i + 1, total: order.length + 1, label: LABELS[id] || id });
        const { data } = await supabase.functions.invoke("seo-fix-issue", { body: { articleId, issueId: id } });
        const field = data?.field as keyof BlogArticle | undefined;
        const value = data?.value;
        if (field && typeof value === "string") {
          setArticle((prev) => prev ? { ...prev, [field]: value } as BlogArticle : prev);
        }
      }
      setFixAllProgress({ current: order.length + 1, total: order.length + 1, label: "Maillage interne + externe…" });
      const { data: linkData } = await supabase.functions.invoke("seo-auto-linking", { body: { articleId } });
      if (linkData?.applied === false) toast.warning(`Corrections appliquées, maillage reporté (${linkData.reason || "limite IA"}).`);
      else toast.success("Tout corrigé !");
      await load();
    } catch (e: any) { toast.error(e?.message || "Échec"); }
    finally { setBusyIssue(null); setFixAllProgress(null); }
  };

  const autoLink = async () => {
    setBusyIssue("link");
    try {
      await (supabase as any).from("blog_articles").update({ body_html: article.body_html }).eq("id", articleId);
      const { data, error } = await supabase.functions.invoke("seo-auto-linking", { body: { articleId } });
      if (error) throw error;
      if (data?.applied === false) {
        toast.warning(data.reason || "Limite de requêtes IA atteinte. Réessayez dans quelques instants.");
        return;
      }
      toast.success("Maillage SEO généré");
      await load();
    } catch (e: any) { toast.error(e?.message || "Échec"); }
    finally { setBusyIssue(null); }
  };

  const metaAiFix = async (field: "title" | "meta" | "slug") => {
    if (field === "slug" && slugLocked) {
      toast.warning("Permalink verrouillé — impossible de régénérer le slug d'un article publié.");
      return;
    }
    const map: Record<string, SeoIssueId> = { title: "kw_in_title", meta: "kw_in_meta", slug: "kw_in_slug" };
    setMetaAiBusy(field);
    try {
      const upd: any = { title: article.title, meta_description: article.meta_description };
      if (!slugLocked) upd.slug = article.slug;
      await (supabase as any).from("blog_articles").update(upd).eq("id", articleId);
      const { data } = await supabase.functions.invoke("seo-fix-issue", { body: { articleId, issueId: map[field] } });
      const f = data?.field as keyof BlogArticle | undefined;
      const value = data?.value;
      if (f && typeof value === "string") {
        setArticle((prev) => prev ? { ...prev, [f]: value } as BlogArticle : prev);
      }
      if (data?.applied === false) toast.warning("L'IA n'a pas respecté la contrainte, réessayez.");
      else toast.success("Mis à jour par l'IA");
    } catch (e: any) { toast.error(e?.message || "Échec"); }
    finally { setMetaAiBusy(null); }
  };

  const regenerateBody = async () => {
    if (!confirm("Régénérer entièrement le corps de l'article par IA ?\n\nLe titre, le permalink, les métadonnées et l'auteur seront conservés.")) return;
    setRegeneratingBody(true);
    try {
      const { data, error } = await supabase.functions.invoke("seo-generate-article", {
        body: { regenerate_body_only: true, article_id: articleId },
      });
      if (error) throw error;
      const jobId = data?.job_id;
      if (!jobId) {
        if (data?.applied === false) {
          toast.warning(data?.reason || "Régénération impossible pour le moment.");
        } else if (data?.article) {
          toast.success("Corps de l'article régénéré par IA");
          await load();
        } else {
          toast.warning("Régénération impossible pour le moment.");
        }
        return;
      }
      toast.info("Régénération en cours… cela peut prendre 1 à 3 minutes.");
      const startedAt = Date.now();
      const maxMs = 5 * 60 * 1000;
      while (Date.now() - startedAt < maxMs) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: jobRow } = await (supabase as any)
          .from("blog_article_jobs")
          .select("status, reason")
          .eq("id", jobId)
          .maybeSingle();
        if (!jobRow) continue;
        if (jobRow.status === "done") {
          toast.success("Corps de l'article régénéré par IA");
          await load();
          return;
        }
        if (jobRow.status === "failed") {
          toast.warning(jobRow.reason || "La régénération a échoué.");
          return;
        }
      }
      toast.warning("La régénération prend trop de temps. Réessayez dans un instant.");
    } catch (e: any) {
      toast.error(e?.message || "Échec de la régénération");
    } finally {
      setRegeneratingBody(false);
    }
  };

  const fmtDate = (iso?: string | null) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—";

  const sanitizedPreview = typeof window !== "undefined"
    ? DOMPurify.sanitize(article.body_html || "", {
        ALLOWED_TAGS: ["h1","h2","h3","h4","h5","h6","p","br","hr","ul","ol","li","blockquote","pre","code","strong","em","b","i","u","s","span","div","a","img","figure","figcaption","aside","table","thead","tbody","tr","th","td"],
        ALLOWED_ATTR: ["href","title","target","rel","src","alt","width","height","class","id"],
      })
    : article.body_html;
  const heroImage = images.find((i) => i.slot === 1);
  const altCoverage = images.length ? images.filter((i) => (i.alt_text || "").trim().length > 0).length / images.length : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onBack}>← Retour</Button>
          <Badge variant={article.status === "published" ? "default" : "secondary"}>
            {article.status === "published" ? "Publié" : "Brouillon"}
          </Badge>
          <Badge variant="outline" className="gap-1"><Pencil className="h-3 w-3" /> Édition active</Badge>
          {article.status === "published" && (
            <a href={`/guide/blog/${article.slug}`} target="_blank" rel="noopener" className="text-sm text-primary inline-flex items-center gap-1 hover:underline">
              <Eye className="h-4 w-4" /> Voir en ligne
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {article.status === "published" && (
            <Button variant="outline" onClick={regenerateBody} disabled={regeneratingBody} title="Régénérer le corps par IA (le permalink et les métadonnées sont préservés)">
              <Wand2 className={`mr-2 h-4 w-4 ${regeneratingBody ? "animate-pulse" : ""}`} />
              {regeneratingBody ? "Régénération…" : "Régénérer par IA"}
            </Button>
          )}
          <Button variant="outline" onClick={() => save()} disabled={saving}>
            <Save className="mr-2 h-4 w-4" /> {saving ? "…" : "Enregistrer"}
          </Button>
          {article.status !== "published" ? (
            <Button onClick={publish} disabled={publishing} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Send className="mr-2 h-4 w-4" /> {publishing ? "…" : "Publier"}
            </Button>
          ) : (
            <Button variant="outline" onClick={unpublish}>Repasser en brouillon</Button>
          )}
          <Button variant="ghost" onClick={remove} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" /> Publié&nbsp;: <span className="text-foreground font-medium">{fmtDate(article.published_at)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Dernière modification&nbsp;: <span className="text-foreground font-medium">{fmtDate(article.updated_at)}</span>
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
        <div className="space-y-4 min-w-0">
          <Tabs defaultValue="edit">
            <TabsList>
              <TabsTrigger value="edit"><Pencil className="h-4 w-4 mr-1" />Éditer</TabsTrigger>
              <TabsTrigger value="preview"><Eye className="h-4 w-4 mr-1" />Aperçu</TabsTrigger>
            </TabsList>
            <TabsContent value="edit" className="mt-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Contenu de l'article</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Auteur</label>
                      <Select value={article.author_id ?? "none"} onValueChange={(v) => setArticle({ ...article, author_id: v === "none" ? null : v })}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner un auteur" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Aucun —</SelectItem>
                          {authors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">H1 (titre affiché)</label>
                      <Input value={article.h1} onChange={(e) => setArticle({ ...article, h1: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Extrait (résumé carte)</label>
                    <Textarea rows={2} value={article.excerpt} onChange={(e) => setArticle({ ...article, excerpt: e.target.value })} />
                  </div>
                  <div className="rounded-md bg-muted/40 border border-border px-3 py-2 text-xs">
                    <span className="text-muted-foreground">Permalink&nbsp;:&nbsp;</span>
                    <span className="font-mono text-foreground">/guide/blog/{article.slug}</span>
                    <span className="text-muted-foreground"> · éditable dans le panneau SEO à droite</span>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Corps de l'article</label>
                    <RichTextEditor value={article.body_html} onChange={(html) => setArticle({ ...article, body_html: html })} />
                    <p className="text-xs text-muted-foreground mt-1">Le maillage interne et externe est généré automatiquement par l'IA via le panneau SEO à droite.</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2"><ImageIcon className="h-5 w-5 text-primary" /> Images (1 héro + 4 illustrations)</CardTitle>
                </CardHeader>
                <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5].map((slot) => {
                    const img = images.find((i) => i.slot === slot);
                    return (
                      <div key={slot} className="space-y-2 border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-muted-foreground">
                            {slot === 1 ? "Image principale (héro)" : `Illustration ${slot - 1}`}
                          </p>
                          <Button size="sm" variant="outline" onClick={() => setPickerOpen({ slot })} disabled={regenSlot !== null}>
                            <Images className={`h-3 w-3 mr-1 ${regenSlot === slot ? "animate-pulse" : ""}`} />
                            {regenSlot === slot ? "…" : "Choisir parmi 6"}
                          </Button>
                        </div>
                        {img ? (
                          <img src={img.image_url} alt={img.alt_text} title={img.title} className="w-full h-40 object-cover rounded" loading="lazy" />
                        ) : (
                          <div className="w-full h-40 rounded bg-muted flex items-center justify-center text-muted-foreground text-sm">Aucune image</div>
                        )}
                        <ImageDropZone onUpload={(f) => uploadImage(slot, f)} label="Remplacer (JPG/PNG/WEBP)" height="h-16" />
                        {img && (
                          <>
                            <Input defaultValue={img.alt_text} onBlur={(e) => updateImageField(img.id, { alt_text: e.target.value })} placeholder="ALT (SEO)" />
                            <Input defaultValue={img.title} onBlur={(e) => updateImageField(img.id, { title: e.target.value })} placeholder="TITLE (SEO)" />
                          </>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="preview" className="mt-4">
              <Card>
                <CardContent className="p-6 sm:p-10 bg-background">
                  <article className="mx-auto max-w-[720px]">
                    <div className="article-prose">
                      <h1>{article.h1 || article.title}</h1>
                    </div>
                    <div className="mt-3 mb-2">
                      <ArticleAuthorHeader authorId={article.author_id} publishedAt={article.published_at} />
                    </div>
                    {heroImage && (
                      <figure className="my-6">
                        <img src={heroImage.image_url} alt={heroImage.alt_text || article.title}
                          className="w-full h-64 sm:h-96 object-cover rounded-xl" loading="lazy" />
                      </figure>
                    )}
                    <div className="article-prose" dangerouslySetInnerHTML={{ __html: sanitizedPreview }} />
                  </article>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <MetaPanel
            title={article.title} slug={article.slug} metaDescription={article.meta_description}
            keyword={seoKeyword} secondaryKeyword={secondaryKeyword}
            onTitle={(v) => setArticle({ ...article, title: v })}
            onSlug={(v) => setArticle({ ...article, slug: v })}
            onMeta={(v) => setArticle({ ...article, meta_description: v })}
            onKeyword={(v) => persistKeyword(v)}
            onSecondaryKeyword={(v) => persistSecondary(v)}
            onAiFix={metaAiFix}
            aiBusy={metaAiBusy}
            slugLocked={slugLocked}
          />
          <SeoScorePanel
            keyword={seoKeyword}
            title={article.title} h1={article.h1} slug={article.slug}
            metaDescription={article.meta_description}
            bodyHtml={article.body_html}
            imagesAltCoverage={altCoverage}
            onFixIssue={fixIssue}
            onFixAll={fixAll}
            onAutoLink={autoLink}
            busyIssue={busyIssue}
            fixAllProgress={fixAllProgress}
          />
        </aside>
      </div>

      <ImageGridPicker
        open={pickerOpen.slot !== null}
        onOpenChange={(v) => { if (!v) setPickerOpen({ slot: null }); }}
        defaultQuery={seoKeyword || article.keywords?.[0] || article.title}
        onSelect={(p) => pickerOpen.slot && onPickPhoto(pickerOpen.slot, p)}
      />
    </div>
  );
};

export default ArticleAudit;